import React, { useState } from 'react';
import { Shield, AlertTriangle, Eye, EyeOff, Scan, CheckCircle } from 'lucide-react';

interface PIIEntity {
  category: string;
  subcategory?: string;
  confidence: number;
  length: number;
}

interface PIIDetectionResult {
  originalLength: number;
  redactedText: string;
  entitiesDetected: number;
  entities: PIIEntity[];
  piiCategories: string[];
}

interface PIIScannerProps {
  evidenceId?: string;
  initialText?: string;
  onScanComplete?: (result: PIIDetectionResult) => void;
}

// Map PII categories to colors and icons
const categoryStyles: Record<string, { color: string; bgColor: string; label: string }> = {
  Person: { color: 'text-blue-700', bgColor: 'bg-blue-100', label: 'Person' },
  USSocialSecurityNumber: { color: 'text-red-700', bgColor: 'bg-red-100', label: 'SSN' },
  PhoneNumber: { color: 'text-green-700', bgColor: 'bg-green-100', label: 'Phone' },
  Email: { color: 'text-purple-700', bgColor: 'bg-purple-100', label: 'Email' },
  Address: { color: 'text-orange-700', bgColor: 'bg-orange-100', label: 'Address' },
  DateTime: { color: 'text-gray-700', bgColor: 'bg-gray-100', label: 'Date' },
  CreditCardNumber: { color: 'text-red-700', bgColor: 'bg-red-100', label: 'Credit Card' },
  Organization: { color: 'text-teal-700', bgColor: 'bg-teal-100', label: 'Organization' },
  PersonType: { color: 'text-indigo-700', bgColor: 'bg-indigo-100', label: 'Person Type' },
};

const getStyle = (category: string) => categoryStyles[category] || { color: 'text-gray-700', bgColor: 'bg-gray-100', label: category };

export const PIIScanner: React.FC<PIIScannerProps> = ({ 
  // evidenceId reserved for future use (scanning stored evidence)
  initialText = '',
  onScanComplete 
}) => {
  const [text, setText] = useState(initialText);
  const [result, setResult] = useState<PIIDetectionResult | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [showRedacted, setShowRedacted] = useState(true);
  const [redactionPolicy, setRedactionPolicy] = useState<'entityType' | 'mask' | 'remove'>('entityType');
  const [error, setError] = useState<string | null>(null);

  const getApiBaseUrl = (): string => {
    const hostname = window.location.hostname;
    if (hostname.includes('azurestaticapps.net') || hostname.includes('dems') || hostname.includes('justicecounty')) {
      return 'https://func-dems-api-dev.azurewebsites.net/api';
    }
    return '/api';
  };

  const scanForPII = async () => {
    if (!text.trim()) {
      setError('Please enter text to scan');
      return;
    }

    setIsScanning(true);
    setError(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/pii/detect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, redactionPolicy })
      });

      if (!response.ok) {
        throw new Error('PII detection failed');
      }

      const data: PIIDetectionResult = await response.json();
      setResult(data);
      onScanComplete?.(data);
    } catch (err) {
      setError('Failed to scan for PII. Please try again.');
      console.error('PII scan error:', err);
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Shield className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">PII Detection Scanner</h3>
          <p className="text-sm text-gray-500">Detect and redact personally identifiable information</p>
        </div>
      </div>

      {/* Input Section */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Text to Analyze
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Enter or paste text content to scan for PII..."
          className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
      </div>

      {/* Options */}
      <div className="flex items-center gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Redaction Mode</label>
          <select
            value={redactionPolicy}
            onChange={(e) => setRedactionPolicy(e.target.value as typeof redactionPolicy)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="entityType">[ENTITY_TYPE]</option>
            <option value="mask">Mask (****)</option>
            <option value="remove">Remove</option>
          </select>
        </div>
        
        <button
          onClick={scanForPII}
          disabled={isScanning || !text.trim()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          <Scan className={`w-4 h-4 ${isScanning ? 'animate-spin' : ''}`} />
          {isScanning ? 'Scanning...' : 'Scan for PII'}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="border-t pt-4">
          {/* Summary */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {result.entitiesDetected > 0 ? (
                <>
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <span className="font-medium text-amber-700">
                    {result.entitiesDetected} PII {result.entitiesDetected === 1 ? 'entity' : 'entities'} detected
                  </span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-green-700">No PII detected</span>
                </>
              )}
            </div>
            
            {result.entitiesDetected > 0 && (
              <button
                onClick={() => setShowRedacted(!showRedacted)}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
              >
                {showRedacted ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showRedacted ? 'Show Original' : 'Show Redacted'}
              </button>
            )}
          </div>

          {/* PII Categories Found */}
          {result.piiCategories.length > 0 && (
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 mb-2">Categories Found</label>
              <div className="flex flex-wrap gap-2">
                {result.piiCategories.map((cat) => {
                  const style = getStyle(cat);
                  return (
                    <span
                      key={cat}
                      className={`px-2 py-1 rounded-full text-xs font-medium ${style.bgColor} ${style.color}`}
                    >
                      {style.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Entity Details */}
          {result.entities.length > 0 && (
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 mb-2">Detected Entities</label>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {result.entities.map((entity, idx) => {
                  const style = getStyle(entity.category);
                  return (
                    <div key={idx} className="flex items-center justify-between text-sm py-1 px-2 bg-gray-50 rounded">
                      <span className={`font-medium ${style.color}`}>{style.label}</span>
                      <span className="text-gray-500">
                        {entity.subcategory && <span className="mr-2">({entity.subcategory})</span>}
                        {Math.round(entity.confidence * 100)}% confidence
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Redacted Text Output */}
          {result.entitiesDetected > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                {showRedacted ? 'Redacted Output' : 'Original Text'}
              </label>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 font-mono text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">
                {showRedacted ? result.redactedText : text}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Compact PII badge for evidence lists
interface PIIBadgeProps {
  categories: string[];
  count?: number;
}

export const PIIBadge: React.FC<PIIBadgeProps> = ({ categories, count }) => {
  if (categories.length === 0) return null;

  const highRiskCategories = ['USSocialSecurityNumber', 'CreditCardNumber', 'USBankAccountNumber'];
  const hasHighRisk = categories.some(c => highRiskCategories.includes(c));

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      hasHighRisk ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
    }`}>
      <Shield className="w-3 h-3" />
      {count ? `${count} PII` : 'PII'} 
      {hasHighRisk && <AlertTriangle className="w-3 h-3" />}
    </div>
  );
};

export default PIIScanner;
